import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "../BrandMark";

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <main className="min-h-full">
      <div className="mx-auto w-full max-w-md px-4 pt-10 pb-8 sm:px-6 sm:pt-16 safe-top">
        <div className="flex justify-center mb-6">
          <BrandMark variant="horizontal" className="h-16 w-auto" />
        </div>
        <div className="space-y-2 mb-6 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-fg">{title}</h1>
          {subtitle && <p className="text-sm text-fg-muted">{subtitle}</p>}
        </div>
        <div className="bg-card border border-outline rounded-2xl p-5 space-y-4 shadow-card">
          {children}
        </div>
        <footer className="mt-6 flex flex-col items-center gap-2 text-center">
          <p className="text-[10px] uppercase tracking-widest text-fg-faint">
            PPLearning · English Trainer for Private Pilot Licence
          </p>
          {/*
            § 5 TMG requires the Impressum to be reachable in ≤ 2 clicks from
            any page — including the unauthenticated login funnel.
          */}
          <nav className="flex gap-3 text-xs text-accent flex-wrap justify-center" aria-label="Rechtliches">
            <Link to="/impressum" data-testid="footer-imprint" className="hover:underline">
              Impressum
            </Link>
            <span className="text-fg-faintest" aria-hidden>·</span>
            <Link to="/datenschutz" data-testid="footer-privacy" className="hover:underline">
              Datenschutz
            </Link>
            <span className="text-fg-faintest" aria-hidden>·</span>
            <Link to="/haftung" data-testid="footer-disclaimer" className="hover:underline">
              Haftung
            </Link>
          </nav>
          <p className="text-[10px] text-fg-faint tracking-wide">
            © 2026 Concept4IT Consulting GmbH
          </p>
        </footer>
      </div>
    </main>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  children: ReactNode;
  hint?: string;
}

export function Field({ label, htmlFor, children, hint }: FieldProps) {
  return (
    <label htmlFor={htmlFor} className="block space-y-1.5">
      <span className="text-sm font-medium text-fg-soft">{label}</span>
      {children}
      {hint && <span className="block text-xs text-fg-faint">{hint}</span>}
    </label>
  );
}

interface FormErrorProps {
  message: string;
}

export function FormError({ message }: FormErrorProps) {
  return (
    <div
      role="alert"
      className="bg-danger-soft border border-danger/40 rounded-xl px-3 py-2 text-sm text-danger"
      data-testid="form-error"
    >
      {message}
    </div>
  );
}

interface PrimaryButtonProps {
  type?: "submit" | "button";
  disabled?: boolean;
  pending?: boolean;
  children: ReactNode;
  testid?: string;
}

export function PrimaryButton({
  type = "submit",
  disabled,
  pending,
  children,
  testid,
}: PrimaryButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || pending}
      data-testid={testid}
      className="w-full min-h-[52px] rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
    >
      {pending ? "…" : children}
    </button>
  );
}

const INPUT_CLASS =
  "w-full min-h-[48px] bg-bg border border-outline rounded-xl px-3 text-base text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:opacity-60";

export const inputClass = INPUT_CLASS;
